import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeCaptureDraftLook = 'capture-draft-copy' | 'capture-draft-mail' | 'capture-draft-dismiss';

const CAPTURE_DRAFT_LOOKS = new Set<string>(['capture-draft-copy', 'capture-draft-mail', 'capture-draft-dismiss']);
export const isWedgeCaptureDraftLook = (look: string | undefined): look is WedgeCaptureDraftLook => look !== undefined && CAPTURE_DRAFT_LOOKS.has(look);

/* Released follow-up draft card (the zero-input draft after a logged outcome): three blocks in a row, each at least 44px tall with a 10px radius,
   12.5px bold type, 0.4px tracking, centered text, the pointer and no text selection. Copy and Open in Mail share one flex of 1; Dismiss takes 0.6.
   Copy sits on the raised fill with a hairline and takes the ink of the card it sits in: the shipped source names a text role that no scope defines,
   so the ink is inherited and this look sets only `inherit`. Open in Mail is the accent fill with the surface ink and no border. Dismiss has no fill
   and no border and uses the tertiary label ink. All read the exact legacy roles, so the shipped ink rules keep matching the serialized text. */
const MIN_HEIGHT = 44, RADIUS = 10, FONT_SIZE = 12.5, TRACKING = 0.4;

export function wedgeCaptureDraftStyle(look: WedgeCaptureDraftLook): CSSProperties {
  const r = wedgeExactRole;
  const base: CSSProperties = {
    flex: 1, minHeight: MIN_HEIGHT, padding: '10px 0', borderRadius: RADIUS, fontSize: FONT_SIZE, fontWeight: 700,
    letterSpacing: TRACKING, textAlign: 'center', cursor: 'pointer', userSelect: 'none', fontFamily: r('font'),
  };
  switch (look) {
    case 'capture-draft-copy': return {...base, background: r('bgElev'), border: `1px solid ${r('cardBorder')}`, color: 'inherit'};
    case 'capture-draft-mail': return {...base, background: r('accent'), border: 0, color: r('surface')};
    case 'capture-draft-dismiss': return {...base, flex: 0.6, background: 'none', border: 'none', color: r('labelTer')};
  }
}
